import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Building2,
  Users,
  FileCheck,
  TrendingUp,
  DollarSign,
  Crown,
  Clock,
  Ban,
  CheckCircle2,
  AlertTriangle,
  Search,
  RefreshCw,
  Loader2,
  Calendar,
  Eye,
  Shield,
  Activity,
  BarChart3,
  ArrowUpRight,
  ArrowDownRight,
  X,
  Unlock,
  Mail
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const SuperadminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [metrics, setMetrics] = useState(null);
  const [recentCompanies, setRecentCompanies] = useState([]);
  const [subscriptionBreakdown, setSubscriptionBreakdown] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [activatingCompany, setActivatingCompany] = useState(null);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [companyToActivate, setCompanyToActivate] = useState(null);

  const fetchDashboardData = useCallback(async () => {
    try {
      // Use credentials: 'include' for httpOnly cookie authentication
      const fetchOptions = {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      };

      // Fetch all data in parallel
      const [metricsRes, companiesRes, breakdownRes] = await Promise.all([
        fetch(`${API}/api/superadmin/dashboard/metrics`, fetchOptions),
        fetch(`${API}/api/superadmin/dashboard/recent-companies?limit=8`, fetchOptions),
        fetch(`${API}/api/superadmin/dashboard/subscription-breakdown`, fetchOptions)
      ]);

      if (metricsRes.ok) {
        const data = await metricsRes.json();
        setMetrics(data);
      } else {
        console.error('Metrics error:', metricsRes.status);
      }

      if (companiesRes.ok) {
        const data = await companiesRes.json();
        setRecentCompanies(data);
      } else {
        console.error('Companies error:', companiesRes.status);
      }

      if (breakdownRes.ok) {
        const data = await breakdownRes.json();
        setSubscriptionBreakdown(data);
      } else {
        console.error('Breakdown error:', breakdownRes.status);
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Erro ao carregar dados do painel');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    // Check if user is superadmin
    if (user && user.role !== 'superadmin') {
      toast.error('Acesso negado. Apenas superadmins podem acessar este painel.');
      navigate('/');
      return;
    }
    
    fetchDashboardData();
  }, [user, navigate, fetchDashboardData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
  };

  // Search companies by name, CNPJ, or admin email
  const handleSearch = useCallback(async (term) => {
    if (!term || term.length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    try {
      const res = await fetch(
        `${API}/api/superadmin/dashboard/companies?search=${encodeURIComponent(term)}&limit=20`,
        {
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.companies || []);
        setShowSearchResults(true);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      handleSearch(searchTerm);
    }, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm, handleSearch]);

  const clearSearch = () => {
    setSearchTerm('');
    setSearchResults([]);
    setShowSearchResults(false);
  };

  // Activate company access
  const handleActivateClick = (company) => {
    setCompanyToActivate(company);
    setShowActivateModal(true);
  };

  const confirmActivateAccess = async () => {
    if (!companyToActivate) return;

    setActivatingCompany(companyToActivate.id);
    try {
      const res = await fetch(
        `${API}/api/superadmin/companies/${companyToActivate.id}/activate`,
        {
          method: 'PATCH',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (res.ok) {
        toast.success('Acesso liberado com sucesso!');
        // Refresh data
        fetchDashboardData();
        if (searchTerm) {
          handleSearch(searchTerm);
        }
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erro ao liberar acesso');
      }
    } catch (error) {
      console.error('Activate error:', error);
      toast.error('Erro ao liberar acesso');
    } finally {
      setActivatingCompany(null);
      setShowActivateModal(false);
      setCompanyToActivate(null);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('pt-BR');
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status, isBlocked) => {
    if (isBlocked) {
      return <Badge className="bg-red-100 text-red-800">Bloqueada</Badge>;
    }
    
    const statusMap = {
      'trial': { label: 'Trial', className: 'bg-blue-100 text-blue-800' },
      'active': { label: 'Ativa', className: 'bg-emerald-100 text-emerald-800' },
      'cancelled': { label: 'Cancelada', className: 'bg-amber-100 text-amber-800' },
      'expired': { label: 'Expirada', className: 'bg-gray-100 text-gray-800' }
    };
    
    const s = statusMap[status] || { label: status || 'Trial', className: 'bg-gray-100 text-gray-800' };
    return <Badge className={s.className}>{s.label}</Badge>;
  };

  const getPlanBadge = (plan) => {
    const planMap = {
      'starter': { label: 'Starter', className: 'bg-slate-100 text-slate-800' },
      'professional': { label: 'Professional', className: 'bg-indigo-100 text-indigo-800' },
      'enterprise': { label: 'Enterprise', className: 'bg-purple-100 text-purple-800' },
      'master': { label: 'Master', className: 'bg-amber-100 text-amber-800' }
    };
    
    const p = planMap[plan] || planMap['starter'];
    return <Badge variant="outline" className={p.className}>{p.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#0d9488] mx-auto mb-4" />
          <p className="text-slate-600">Carregando painel global...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="superadmin-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1e3a5f] to-[#0d9488] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/10 rounded-xl">
                <Shield className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-2xl font-bold">Painel Global</h1>
                <p className="text-white/80 text-sm">Administração da Plataforma CERP Brasil</p>
              </div>
            </div>
            <Button
              variant="outline"
              className="border-white/30 text-white hover:bg-white/10"
              onClick={handleRefresh}
              disabled={refreshing}
            >
              {refreshing ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="h-4 w-4 mr-2" />
              )}
              Atualizar
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Search Bar - Company Management */}
        <Card className="border-0 shadow-sm bg-white">
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <Search className="h-5 w-5 text-[#0d9488]" />
                <h3 className="font-semibold text-[#1e3a5f]">Buscar Empresa</h3>
              </div>
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Buscar por nome, CNPJ ou email do admin..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-10 h-11 border-slate-200 focus:border-[#0d9488] focus:ring-[#0d9488]"
                    data-testid="company-search-input"
                  />
                  {searchTerm && (
                    <button
                      onClick={clearSearch}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-slate-100 rounded-full"
                    >
                      <X className="h-4 w-4 text-slate-400" />
                    </button>
                  )}
                </div>
                {isSearching && (
                  <div className="absolute right-12 top-1/2 -translate-y-1/2">
                    <Loader2 className="h-4 w-4 animate-spin text-[#0d9488]" />
                  </div>
                )}
              </div>

              {/* Search Results */}
              {showSearchResults && searchResults.length > 0 && (
                <div className="border rounded-lg divide-y max-h-96 overflow-y-auto">
                  {searchResults.map((company) => (
                    <div 
                      key={company.id}
                      className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#1e3a5f] rounded-lg flex items-center justify-center text-white font-bold text-sm">
                          {(company.nome || 'E')[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{company.nome || 'Sem nome'}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span>{company.cnpj || 'CNPJ não informado'}</span>
                            {company.admin?.email && (
                              <>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                  <Mail className="h-3 w-3" />
                                  {company.admin.email}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getPlanBadge(company.subscription_plan)}
                        {getStatusBadge(company.subscription_status, company.is_blocked)}
                        {company.subscription_status !== 'active' && !company.is_blocked && (
                          <Button
                            size="sm"
                            onClick={() => handleActivateClick(company)}
                            disabled={activatingCompany === company.id}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white ml-2"
                            data-testid={`activate-btn-${company.id}`}
                          >
                            {activatingCompany === company.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <>
                                <Unlock className="h-4 w-4 mr-1" />
                                Liberar Acesso
                              </>
                            )}
                          </Button>
                        )}
                        {company.subscription_status === 'active' && (
                          <Badge className="bg-emerald-100 text-emerald-800 ml-2">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Acesso Ativo
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {showSearchResults && searchTerm && searchResults.length === 0 && !isSearching && (
                <div className="text-center py-8 text-slate-500 border rounded-lg">
                  <Building2 className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>Nenhuma empresa encontrada para &quot;{searchTerm}&quot;</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Main Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Companies */}
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total de Empresas</p>
                  <p className="text-3xl font-bold text-[#1e3a5f]">
                    {metrics?.totals?.companies || 0}
                  </p>
                  {metrics?.this_month?.new_companies > 0 && (
                    <p className="text-xs text-emerald-600 flex items-center mt-1">
                      <ArrowUpRight className="h-3 w-3 mr-1" />
                      +{metrics.this_month.new_companies} este mês
                    </p>
                  )}
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Building2 className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Total Employees */}
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total de Funcionários</p>
                  <p className="text-3xl font-bold text-[#1e3a5f]">
                    {metrics?.totals?.employees || 0}
                  </p>
                  {metrics?.this_month?.new_employees > 0 && (
                    <p className="text-xs text-emerald-600 flex items-center mt-1">
                      <ArrowUpRight className="h-3 w-3 mr-1" />
                      +{metrics.this_month.new_employees} este mês
                    </p>
                  )}
                </div>
                <div className="p-3 bg-emerald-100 rounded-lg">
                  <Users className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* MRR */}
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500">MRR (Receita Mensal)</p>
                  <p className="text-3xl font-bold text-[#0d9488]">
                    {formatCurrency(metrics?.revenue?.mrr || 0)}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    ARR: {formatCurrency(metrics?.revenue?.arr || 0)}
                  </p>
                </div>
                <div className="p-3 bg-teal-100 rounded-lg">
                  <DollarSign className="h-6 w-6 text-teal-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Assessments */}
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm text-slate-500">Avaliações no Mês</p>
                  <p className="text-3xl font-bold text-[#1e3a5f]">
                    {metrics?.this_month?.assessments || 0}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Total: {metrics?.totals?.assessments || 0}
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <FileCheck className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Risks and Action Plans Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Risks Summary */}
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#1e3a5f] flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-orange-500" />
                  Riscos Cadastrados
                </h3>
                <Badge className="bg-slate-100 text-slate-700">
                  Total: {metrics?.risks?.total || 0}
                </Badge>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div className="text-center p-3 bg-red-50 rounded-lg">
                  <p className="text-xl font-bold text-red-700">{metrics?.risks?.by_level?.critico || 0}</p>
                  <p className="text-xs text-red-600">Crítico</p>
                </div>
                <div className="text-center p-3 bg-orange-50 rounded-lg">
                  <p className="text-xl font-bold text-orange-700">{metrics?.risks?.by_level?.alto || 0}</p>
                  <p className="text-xs text-orange-600">Alto</p>
                </div>
                <div className="text-center p-3 bg-yellow-50 rounded-lg">
                  <p className="text-xl font-bold text-yellow-700">{metrics?.risks?.by_level?.medio || 0}</p>
                  <p className="text-xs text-yellow-600">Médio</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-xl font-bold text-green-700">{metrics?.risks?.by_level?.baixo || 0}</p>
                  <p className="text-xs text-green-600">Baixo</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Plans Summary */}
          <Card className="border-0 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-[#1e3a5f] flex items-center gap-2">
                  <Activity className="h-5 w-5 text-blue-500" />
                  Planos de Ação
                </h3>
                <Badge className="bg-slate-100 text-slate-700">
                  Total: {metrics?.action_plans?.total || 0}
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-emerald-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                    <span className="text-sm text-emerald-700">Concluídos</span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-800">{metrics?.action_plans?.completed || 0}</p>
                </div>
                <div className="p-4 bg-amber-50 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="h-4 w-4 text-amber-600" />
                    <span className="text-sm text-amber-700">Pendentes</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-800">{metrics?.action_plans?.pending || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Subscription Status Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-0 shadow-sm bg-blue-50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-2xl font-bold text-blue-800">{metrics?.subscriptions?.trial || 0}</p>
                  <p className="text-xs text-blue-600">Em Trial</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-emerald-50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <div>
                  <p className="text-2xl font-bold text-emerald-800">{metrics?.subscriptions?.active || 0}</p>
                  <p className="text-xs text-emerald-600">Ativas</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-amber-50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
                <div>
                  <p className="text-2xl font-bold text-amber-800">{metrics?.subscriptions?.cancelled || 0}</p>
                  <p className="text-xs text-amber-600">Canceladas</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-gray-50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <Calendar className="h-5 w-5 text-gray-600" />
                <div>
                  <p className="text-2xl font-bold text-gray-800">{metrics?.subscriptions?.expired || 0}</p>
                  <p className="text-xs text-gray-600">Expiradas</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm bg-red-50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <Ban className="h-5 w-5 text-red-600" />
                <div>
                  <p className="text-2xl font-bold text-red-800">{metrics?.subscriptions?.blocked || 0}</p>
                  <p className="text-xs text-red-600">Bloqueadas</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Companies - Takes 2 columns */}
          <Card className="lg:col-span-2 border-0 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-[#0d9488]" />
                    Empresas Recentes
                  </CardTitle>
                  <CardDescription>Últimas empresas cadastradas na plataforma</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {recentCompanies.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Building2 className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>Nenhuma empresa cadastrada ainda</p>
                  </div>
                ) : (
                  recentCompanies.map((company) => (
                    <div 
                      key={company.id} 
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-[#1e3a5f] rounded-lg flex items-center justify-center text-white font-bold text-sm">
                          {(company.nome || 'E')[0].toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-slate-800">{company.nome || 'Sem nome'}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span>{company.cnpj || 'CNPJ não informado'}</span>
                            <span>•</span>
                            <span>{company.employee_count || 0} funcionários</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {getPlanBadge(company.subscription_plan)}
                        {getStatusBadge(company.subscription_status, company.is_blocked)}
                        {company.subscription_status !== 'active' && !company.is_blocked && (
                          <Button
                            size="sm"
                            onClick={() => handleActivateClick(company)}
                            disabled={activatingCompany === company.id}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs px-2 py-1 h-7"
                            data-testid={`activate-recent-btn-${company.id}`}
                          >
                            {activatingCompany === company.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <>
                                <Unlock className="h-3 w-3 mr-1" />
                                Liberar
                              </>
                            )}
                          </Button>
                        )}
                        {company.subscription_status === 'active' && (
                          <Badge className="bg-emerald-100 text-emerald-800 text-xs">
                            <CheckCircle2 className="h-3 w-3 mr-1" />
                            Ativo
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Revenue by Plan */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-[#0d9488]" />
                Receita por Plano
              </CardTitle>
              <CardDescription>MRR dividido por tipo de plano</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {subscriptionBreakdown && Object.entries(subscriptionBreakdown).map(([plan, data]) => (
                  <div key={plan} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getPlanBadge(plan)}
                        <span className="text-sm text-slate-500">
                          ({data.active || 0} ativas)
                        </span>
                      </div>
                      <span className="font-semibold text-[#1e3a5f]">
                        {formatCurrency(data.mrr || 0)}
                      </span>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-[#0d9488] to-[#1e3a5f] rounded-full transition-all"
                        style={{ 
                          width: `${metrics?.revenue?.mrr > 0 
                            ? ((data.mrr || 0) / metrics.revenue.mrr * 100) 
                            : 0}%` 
                        }}
                      />
                    </div>
                  </div>
                ))}

                {(!subscriptionBreakdown || Object.keys(subscriptionBreakdown).length === 0) && (
                  <div className="text-center py-8 text-slate-500">
                    <DollarSign className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>Sem dados de receita</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Activity Stats */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="h-5 w-5 text-[#0d9488]" />
              Atividade do Mês
            </CardTitle>
            <CardDescription>Resumo de atividades na plataforma este mês</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-[#1e3a5f]">
                  {metrics?.this_month?.new_companies || 0}
                </p>
                <p className="text-sm text-slate-500">Novas Empresas</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-[#1e3a5f]">
                  {metrics?.this_month?.new_employees || 0}
                </p>
                <p className="text-sm text-slate-500">Novos Funcionários</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-[#1e3a5f]">
                  {metrics?.this_month?.dds_views || 0}
                </p>
                <p className="text-sm text-slate-500">DDS Visualizados</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-lg">
                <p className="text-3xl font-bold text-[#1e3a5f]">
                  {metrics?.this_month?.trainings_completed || 0}
                </p>
                <p className="text-sm text-slate-500">Treinamentos Concluídos</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Footer info */}
        <div className="text-center text-xs text-slate-400">
          Última atualização: {metrics?.generated_at ? new Date(metrics.generated_at).toLocaleString('pt-BR') : '-'}
        </div>
      </div>

      {/* Activate Access Confirmation Modal */}
      {showActivateModal && companyToActivate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="activate-modal">
          <Card className="w-full max-w-md mx-4 border-0 shadow-2xl">
            <CardHeader className="pb-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-emerald-100 rounded-xl">
                  <Unlock className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <CardTitle className="text-lg">Liberar Acesso</CardTitle>
                  <CardDescription>Ativar assinatura manualmente</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-800">{companyToActivate.nome}</p>
                <p className="text-sm text-slate-500">{companyToActivate.cnpj}</p>
                {companyToActivate.admin?.email && (
                  <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                    <Mail className="h-3 w-3" />
                    {companyToActivate.admin.email}
                  </p>
                )}
              </div>
              <p className="text-slate-600">
                Deseja liberar o acesso manual para esta empresa?
              </p>
              <p className="text-sm text-slate-500">
                O plano será definido como <strong>Professional</strong> caso não haja um plano selecionado.
              </p>
            </CardContent>
            <div className="flex justify-end gap-3 p-6 pt-0">
              <Button
                variant="outline"
                onClick={() => {
                  setShowActivateModal(false);
                  setCompanyToActivate(null);
                }}
                data-testid="activate-cancel-btn"
              >
                Cancelar
              </Button>
              <Button
                onClick={confirmActivateAccess}
                disabled={activatingCompany === companyToActivate.id}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="activate-confirm-btn"
              >
                {activatingCompany === companyToActivate.id ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 mr-2" />
                )}
                Confirmar
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SuperadminDashboard;
